import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const pageStyle = {
  padding: '40px',
  maxWidth: '800px',
  margin: '0 auto',
  color: '#c9d1d9',
  lineHeight: '1.8',
  textAlign: 'left',
  backgroundColor: '#0d1117',
  minHeight: '100vh',
  fontFamily: 'sans-serif'
};

const h1Style = { color: '#00d4ff', borderBottom: '1px solid #333', paddingBottom: '15px', fontWeight: '300', letterSpacing: '1px' };
const h2Style = { color: '#ffca28', marginTop: '40px', marginBottom: '15px', fontSize: '1.3rem', letterSpacing: '1px' };
const ulStyle = { paddingLeft: '20px', marginBottom: '20px' };
const liStyle = { marginBottom: '10px' };
const linkStyle = { color: '#0d1117', backgroundColor: '#ffca28', padding: '10px 20px', borderRadius: '4px', textDecoration: 'none', marginTop: '40px', display: 'inline-block', fontWeight: 'bold', letterSpacing: '1px' };

export function Terms() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={pageStyle}>

      {/* 👇 TERMS PAGE SEO SHIELD 👇 */}
      <Helmet>
        <title>Terms & Conditions | Adonisian Astrology</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <h1 style={h1Style}>Terms and Conditions for Adonisian Astrology</h1>
      <p style={{ fontStyle: 'italic', color: '#8b949e' }}>Last Updated: June 4, 2026</p>
      <p>Welcome to Adonisian Astrology www.adonisian.com. By accessing or using our website, web application, and related services collectively, the "Service", you agree to be bound by these Terms and Conditions.</p>

      <h2 style={h2Style}>1. Description of Service</h2>
      <p>Adonisian provides astronomically-based astrological chart generation, somatic meditation tracking, and digital content based on a proprietary 13-sign framework. The Service includes both free tiers like the base cosmic inscription and premium, paid tiers like the 13-block Blueprint Grid and deluxe PDF reports.</p>

      <h2 style={h2Style}>2. Informational & Entertainment Purposes Only</h2>
      <ul style={ulStyle}>
        <li style={liStyle}><strong>Astrology & Constellation Data:</strong> All astrological charts, interpretations, and reports provided by the Service are for entertainment, educational, and spiritual exploration purposes only. They do not constitute professional psychological, financial, or legal advice.</li>
        <li style={liStyle}><strong>Health & Wellness Disclaimer:</strong> The somatic exercises, physical nodes, and gamified meditation tracking featured on the Service are designed to support general well-being. They are not a substitute for professional medical advice, diagnosis, or treatment. Always consult with a qualified healthcare provider before beginning any new physical regimen.</li>
      </ul>

      <h2 style={h2Style}>3. User Accounts and Purchases</h2>
      <ul style={ulStyle}>
        <li style={liStyle}><strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials managed via our authentication provider and for all activities that occur under your account.</li>
        <li style={liStyle}><strong>Premium Access:</strong> Access to the full 13-block Blueprint Grid, specific somatic node alignments, and the emailed Deluxe Somatic Report requires a one-time purchase of $99.</li>
        <li style={liStyle}><strong>Payments:</strong> All payments are processed securely through our third-party payment processor Stripe. By submitting payment information, you authorize us and our payment processor to charge the applicable fees.</li>
        <li style={liStyle}><strong>Refunds:</strong> Because the Adonisian Deluxe Somatic Report is instantly generated, digitally delivered via email, and immediately unlocks premium dashboard features, <strong>all sales are final</strong>. We do not offer refunds on digital deliverables.</li>
      </ul>

      <h2 style={h2Style}>4. Intellectual Property, Copyright & Trademark</h2>
      <p>All content, features, and functionality on the Service—including but not limited to the Adonisian Engine Codebase, the 13-Sign Astrological Framework, somatic libraries, visual architecture, text, graphics, logos, algorithms, badge designs, and software—are the exclusive property of Adonisian Astrology and are protected by international copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission.</p>
      <p>The following terms and visual markers are claimed as trademarks: <strong>Adonisian Astrology™, Adonisian Somatic Blueprint™, Adonisian Wheel System™, Spinal Gateway Engine™, Ascendance Progress System™, Adonisian Zodiac™, Adonisian ™, and Astro-Somatic™</strong>.</p>
      <h2 style={h2Style}>5. Gamification and The Ascendance Tracker</h2>
      <p>The Service includes an interactive "Ascendance Tracker" designed to log daily meditation streaks up to a 65-day "Guru Status." This feature is provided for personal motivation and entertainment. You agree to use these features as intended. Adonisian makes no guarantees regarding spiritual, mental, or physical outcomes resulting from reaching "Guru Status," and you agree not to manipulate or artificially inflate your database metrics.</p>

      <h2 style={h2Style}>6. Limitation of Liability</h2>
      <p>To the maximum extent permitted by law, Adonisian Astrology and its creator shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your access to or use of, or inability to access or use, the Service.</p>

      <Link to="/" style={linkStyle}>The Portal 📟</Link>
    </div>
  );
}

export function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={pageStyle}>

      {/* 👇 PRIVACY PAGE SEO SHIELD 👇 */}
      <Helmet>
        <title>Privacy Policy | Adonisian Astrology</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <h1 style={h1Style}>Privacy Policy for Adonisian Astrology</h1>
      <p style={{ fontStyle: 'italic', color: '#8b949e' }}>Last Updated: June 4, 2026</p>
      
      <h2 style={h2Style}>1. Information We Collect</h2>
      <p>To generate your precise cosmic cross-section, the Adonisian Astrology Portal requires specific astronomical and geographic data. We collect:</p>
      <ul style={ulStyle}>
        <li style={liStyle}><strong>Account Data:</strong> Email address and secure authentication tokens managed via Supabase.</li>
        <li style={liStyle}><strong>Birth Coordinates:</strong> Year, month, day, precise hour UTC, and geolocation data latitude and longitude of your birth.</li>
        <li style={liStyle}><strong>Platform Activity:</strong> Engagement metrics, including your 65-day Ascendance meditation logs and unlocked chart history.</li>
        <li style={liStyle}><strong>Payment Data:</strong> Handled entirely by our secure third-party processor Stripe. Adonisian does not store or process your credit card information on our servers.</li>
      </ul>

      <h2 style={h2Style}>2. How We Use Your Data</h2>
      <p>Your data is used strictly to power the Adonisian Engine. We use it to:</p>
      <ul style={ulStyle}>
        <li style={liStyle}>Calculate your 13-sign Somatic Blueprint, Ascendant, and specific planet-to-joint somatic nodes.</li>
        <li style={liStyle}>Generate and email your custom Adonisian Deluxe Somatic Report via our automated delivery system.</li>
        <li style={liStyle}>Track your progress toward "Guru Status" within our meditation tracking system.</li>
        <li style={liStyle}>Authenticate your access to the premium Blueprint Grid.</li>
      </ul>

      <h2 style={h2Style}>3. Third-Party Data Processing</h2>
      <p>We employ industry-leading infrastructure to secure your experience:</p>
      <ul style={ulStyle}>
        <li style={liStyle}><strong>Supabase:</strong> Manages encrypted user authentication, permission catalogs, and database logging.</li>
        <li style={liStyle}><strong>Stripe:</strong> Processes all payments and subscriptions securely.</li>
        <li style={liStyle}><strong>Resend:</strong> Facilitates the secure delivery of your personalized PDF manuals.</li>
      </ul>
      <p>We will never sell your personal or astrological data to third-party data brokers.</p>

      <h2 style={h2Style}>4. Data Retention and Deletion</h2>
      <p>You maintain control over your cosmic map. You may request the deletion of your account and all associated birth coordinates, meditation logs, and unlocked charts at any time by contacting support.</p>

      <Link to="/" style={linkStyle}>The Portal 📟</Link>
    </div>
  );
}